import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RIDEBOOK_CHANNELS, RIDEBOOK_CHANNEL_LABELS, RIDEBOOK_KIND_LABELS, RIDEBOOK_PAYMENT_LABELS, RIDEBOOK_PAYMENT_METHODS, type RidebookEntry, type RidebookKind } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import { euro, errorText, nowLocal, text, toIso, toLocalInput, when } from './shared';

type Form = Record<string, string>;

/** Felder je Art, mit Beschriftung, Eingabeart und ob Pflicht */
const FIELDS: Record<RidebookKind, { key: string; label: string; type: 'text' | 'datetime' | 'number' | 'euro' | 'channel' | 'payment' | 'textarea'; required?: boolean; hint?: string }[]> = {
  received: [
    { key: 'receivedAt', label: 'Auftrag eingegangen am', type: 'datetime', required: true, hint: 'Wann der Auftrag am Betriebssitz angekommen ist' },
    { key: 'receivedVia', label: 'Eingegangen über', type: 'channel', required: true },
    { key: 'passengerName', label: 'Name des Fahrgasts', type: 'text', required: true },
    { key: 'passengerPhone', label: 'Telefon des Fahrgasts', type: 'text' },
    { key: 'pickupAddress', label: 'Abholort', type: 'text', required: true },
    { key: 'destinationAddress', label: 'Zielort', type: 'text', required: true },
    { key: 'pickupAt', label: 'Beginn der Fahrt laut Auftrag', type: 'datetime', required: true },
    { key: 'passengers', label: 'Anzahl der Fahrgäste', type: 'number' },
    { key: 'orderedKm', label: 'Beauftragte Strecke in km', type: 'number' },
    { key: 'agreedPriceCents', label: 'Vereinbarter Preis (brutto)', type: 'euro' },
    { key: 'note', label: 'Bemerkung', type: 'textarea' },
  ],
  assigned: [
    { key: 'assignedAt', label: 'An den Fahrer weitergegeben am', type: 'datetime', required: true },
    { key: 'plate', label: 'Amtliches Kennzeichen', type: 'text', required: true },
    { key: 'driverName', label: 'Name des Fahrers', type: 'text', required: true },
    { key: 'vehiclePosition', label: 'Standort des Fahrzeugs bei der Weitergabe', type: 'text', hint: 'Bei Vermittlung über eine App' },
  ],
  started: [
    { key: 'startedAt', label: 'Fahrt begonnen am', type: 'datetime', required: true },
    { key: 'startPlace', label: 'Ort des Beginns', type: 'text' },
    { key: 'odometerKm', label: 'Kilometerstand', type: 'number' },
  ],
  finished: [
    { key: 'finishedAt', label: 'Fahrt beendet am', type: 'datetime', required: true },
    { key: 'endPlace', label: 'Ort des Endes', type: 'text' },
    { key: 'distanceKm', label: 'Gefahrene Strecke in km', type: 'number' },
    { key: 'odometerKm', label: 'Kilometerstand', type: 'number' },
    { key: 'priceCents', label: 'Fahrpreis (brutto)', type: 'euro' },
    { key: 'paymentMethod', label: 'Zahlungsart', type: 'payment' },
  ],
  cancelled: [
    { key: 'cancelledAt', label: 'Zeitpunkt', type: 'datetime', required: true },
    { key: 'reason', label: 'Grund', type: 'textarea', required: true },
  ],
  correction: [{ key: 'text', label: 'Berichtigung', type: 'textarea', required: true, hint: 'Was richtig ist und warum. Der ursprüngliche Eintrag bleibt stehen.' }],
};

const NOW_FIELDS = ['receivedAt', 'assignedAt', 'startedAt', 'finishedAt', 'cancelledAt'];

function initial(kind: RidebookKind, payload: Record<string, unknown>, isNew: boolean): Form {
  const f: Form = {};
  for (const field of FIELDS[kind]) {
    const v = payload[field.key];
    if (field.type === 'datetime') f[field.key] = toLocalInput(v) || (isNew && NOW_FIELDS.includes(field.key) ? nowLocal() : '');
    else if (field.type === 'euro') f[field.key] = typeof v === 'number' ? (v / 100).toFixed(2).replace('.', ',') : '';
    else f[field.key] = v === undefined || v === null ? (field.type === 'channel' ? 'phone' : '') : String(v);
  }
  return f;
}

function toPayload(kind: RidebookKind, f: Form): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  for (const field of FIELDS[kind]) {
    const v = (f[field.key] ?? '').trim();
    if (!v) continue;
    if (field.type === 'datetime') p[field.key] = toIso(v);
    else if (field.type === 'number') p[field.key] = Number(v.replace(',', '.'));
    else if (field.type === 'euro') p[field.key] = Math.round(Number(v.replace(',', '.')) * 100);
    else p[field.key] = v;
  }
  return p;
}

/**
 * Eintrag für das Auftragsbuch von Hand: Er entsteht als Entwurf, den man speichern, ändern und löschen kann. Erst „Ins Auftragsbuch eintragen“ setzt ihn unveränderlich in die
 * Kette. Dafür öffnet sich ein Fenster, das noch einmal sagt, was das heißt. Entwürfe, die Wirkia aus Aufträgen angelegt hat (weil Angaben zur Fahrt fehlten), laufen genauso.
 */
export function EntryForm({ kind, refId, entry }: { kind: RidebookKind; refId?: string; entry?: RidebookEntry }) {
  const { can } = useSession();
  const navigate = useNavigate();
  const client = useQueryClient();
  const dialog = useRef<HTMLDialogElement>(null);
  const [form, setForm] = useState<Form>(() => initial(kind, entry?.payload ?? {}, !entry));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [understood, setUnderstood] = useState(false);
  const [open, setOpen] = useState(false);
  const [id, setId] = useState<string | null>(entry?.id ?? null);

  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  const set = (key: string) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const refresh = () => client.invalidateQueries({ queryKey: ['ridebook'] });

  const save = useMutation({
    mutationFn: async () => {
      const payload = toPayload(kind, form);
      if (id) return (await api<{ data: RidebookEntry }>('PUT', `/ridebook/drafts/${id}`, { payload })).data;
      return (await api<{ data: RidebookEntry }>('POST', '/ridebook/drafts', { kind, ...(refId ? { refId } : {}), payload })).data;
    },
    onSuccess: (saved) => {
      setId(saved.id);
      setErrors({});
      refresh();
      setMessage({ ok: true, text: 'Der Entwurf ist gespeichert. Er steht noch nicht im Auftragsbuch.' });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Speichern hat nicht geklappt.') }),
  });

  const finalize = useMutation({
    mutationFn: async () => {
      // Erst den aktuellen Stand speichern, dann bestätigen: Bestätigt wird genau das, was auf dem Bildschirm steht
      const saved = await save.mutateAsync();
      return (await api<{ data: RidebookEntry }>('POST', `/ridebook/drafts/${saved.id}/finalize`)).data;
    },
    onSuccess: (done) => {
      setOpen(false);
      refresh();
      navigate(`/auftragsbuch/${done.kind === 'received' ? done.id : (done.refId ?? done.id)}`);
    },
    onError: (e) => {
      setOpen(false);
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        setMessage({ ok: false, text: 'Der Eintrag ist noch nicht vollständig. Bitte prüfen Sie die markierten Felder.' });
      } else setMessage({ ok: false, text: errorText(e, 'Der Eintrag konnte nicht ins Auftragsbuch übernommen werden.') });
    },
  });

  const remove = useMutation({
    mutationFn: () => api('DELETE', `/ridebook/drafts/${id}`),
    onSuccess: () => {
      refresh();
      navigate('/auftragsbuch');
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Löschen hat nicht geklappt.') }),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);
    save.mutate();
  };
  const err = (key: string) => errors[`payload.${key}`];
  const preview = toPayload(kind, form);

  return (
    <>
      <form className="card ridebook-form" onSubmit={submit} noValidate>
        <h2>{RIDEBOOK_KIND_LABELS[kind]}{entry ? '' : ' eintragen'}</h2>
        {entry?.source === 'auto' && entry.status === 'draft' && (
          <div className="alert alert--warn" role="note">
            Wirkia hat diesen Auftragseingang aus einem Auftrag angelegt. Für das Auftragsbuch fehlten Angaben zur Fahrt. Ergänzen Sie sie und tragen Sie den Auftrag ins Buch ein, erst dann gilt er dort.
          </div>
        )}
        <p className="muted small">Der Eintrag ist zuerst ein Entwurf. Sie können ihn speichern, ändern und löschen. Erst mit „Ins Auftragsbuch eintragen“ wird er unveränderlich.</p>
        {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
        <div className="form-grid">
          {FIELDS[kind].map((field) => (
            <label key={field.key} className={`field${field.type === 'textarea' ? ' field--wide' : ''}`}>
              <span>{field.label}{field.required && <> <abbr title="Pflichtfeld">*</abbr></>}</span>
              {field.type === 'channel' ? (
                <select className="input" value={form[field.key]} onChange={set(field.key)}>{RIDEBOOK_CHANNELS.map((c) => <option key={c} value={c}>{RIDEBOOK_CHANNEL_LABELS[c]}</option>)}</select>
              ) : field.type === 'payment' ? (
                <select className="input" value={form[field.key]} onChange={set(field.key)}>
                  <option value="">Nicht angegeben</option>
                  {RIDEBOOK_PAYMENT_METHODS.map((c) => <option key={c} value={c}>{RIDEBOOK_PAYMENT_LABELS[c]}</option>)}
                </select>
              ) : field.type === 'textarea' ? (
                <textarea className="input" rows={3} value={form[field.key]} onChange={set(field.key)} maxLength={1000} />
              ) : (
                <input className="input" type={field.type === 'datetime' ? 'datetime-local' : 'text'} inputMode={field.type === 'number' || field.type === 'euro' ? 'decimal' : undefined} value={form[field.key]} onChange={set(field.key)} aria-invalid={!!err(field.key)} />
              )}
              {field.hint && <small className="muted">{field.hint}</small>}
              {err(field.key) && <small className="field-error" role="alert">{err(field.key)}</small>}
            </label>
          ))}
        </div>
        <div className="form-actions">
          <button type="submit" className="btn btn--secondary" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : 'Entwurf speichern'}</button>
          {can('ridebook.finalize') && (
            <button type="button" className="btn btn--primary" disabled={save.isPending || finalize.isPending} onClick={() => { setMessage(null); setUnderstood(false); setOpen(true); }}>
              Ins Auftragsbuch eintragen …
            </button>
          )}
          {id && <button type="button" className="btn btn--danger" disabled={remove.isPending} onClick={() => window.confirm('Den Entwurf löschen? Er stand noch nicht im Auftragsbuch.') && remove.mutate()}>Entwurf löschen</button>}
        </div>
        {!can('ridebook.finalize') && <p className="muted small">Ins Auftragsbuch eintragen darf, wer das Recht „bestätigen“ hat. Speichern Sie den Entwurf, die Inhaberin oder der Inhaber trägt ihn ein.</p>}
      </form>

      <dialog ref={dialog} className="dialog dialog--wide" aria-labelledby="ridebook-confirm-title" onClose={() => setOpen(false)} onClick={(e) => { if (e.target === dialog.current && !finalize.isPending) setOpen(false); }}>
        <div className="dialog__body">
          <h2 id="ridebook-confirm-title">Ins Auftragsbuch eintragen?</h2>
          <div className="alert alert--warn" role="note">
            <strong>Danach lässt sich der Eintrag nicht mehr ändern und nicht löschen.</strong> Das Auftragsbuch ist nach § 49 Abs. 4 PBefG lückenlos und unveränderlich zu führen. Der Eintrag bekommt eine durchlaufende
            Nummer, den Zeitpunkt der Erfassung und einen Hash, der ihn mit dem vorherigen Eintrag verbindet. Ein Fehler lässt sich später nur mit einem neuen Eintrag berichtigen, der den alten bestehen lässt.
          </div>
          <dl className="ridebook-facts">
            {FIELDS[kind].filter((f) => preview[f.key] !== undefined).map((f) => (
              <div key={f.key}>
                <dt>{f.label}</dt>
                <dd>{f.type === 'datetime' ? when(preview[f.key]) : f.type === 'euro' ? euro(preview[f.key]) : f.type === 'channel' ? RIDEBOOK_CHANNEL_LABELS[preview[f.key] as keyof typeof RIDEBOOK_CHANNEL_LABELS] : f.type === 'payment' ? RIDEBOOK_PAYMENT_LABELS[preview[f.key] as keyof typeof RIDEBOOK_PAYMENT_LABELS] : text(preview[f.key]) || String(preview[f.key])}</dd>
              </div>
            ))}
          </dl>
          <label className="check">
            <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} disabled={finalize.isPending} />
            <span>Ich habe die Angaben geprüft. Mir ist klar, dass der Eintrag danach unveränderlich ist.</span>
          </label>
          <div className="form-actions">
            <button type="button" className="btn btn--primary" disabled={!understood || finalize.isPending} onClick={() => finalize.mutate()}>{finalize.isPending ? 'Wird eingetragen …' : 'Unveränderlich eintragen'}</button>
            <button type="button" className="btn btn--secondary" disabled={finalize.isPending} onClick={() => setOpen(false)}>Zurück zum Entwurf</button>
          </div>
        </div>
      </dialog>
    </>
  );
}
